// components/user/KycSection.tsx
import React from "react";
import KycForm from "./KycForm";
import { KycFormData, UserProfile } from "../../types";
import { formatDate } from "../../lib/utils";
import Button from "../../components/ui/Button";

interface KycSectionProps {
  isKycSubmitted: boolean | undefined;
  kycSubmissionDate?: string | null;
  onSubmit: (data: KycFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  profile: UserProfile | null;
  isProfile?: boolean;
}

const KycSection: React.FC<KycSectionProps> = ({
  isKycSubmitted,
  kycSubmissionDate,
  onSubmit,
  profile,
  isLoading,
  error,
  isProfile = true,
}) => {

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    onSubmit({
      pan_number: profile.pan_number || "",
      aadhar_number: profile.aadhar_number || "",
      dob: profile.dob || "",
    });
    window.location.reload()

  };

  return (
    <div className="h-full flex flex-col">
      {/* Section Title with Icon */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center mb-6 space-y-3 sm:space-y-0">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-golden-accent/20 rounded-full flex items-center justify-center sm:mr-4 flex-shrink-0">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-golden-accent" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="text-center sm:text-left min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-text-primary">KYC Verification</h2>
          <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Complete your identity verification</p>
        </div>
      </div>

      {isKycSubmitted ? (
        <div className="flex-1 flex flex-col">
          {/* Success Header */}
          <div className="bg-functional-success/10 rounded-xl p-6 border border-functional-success/20 mb-6">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-functional-success/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-functional-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-functional-success">
                  KYC Details Submitted
                </h3>
                <p className="text-functional-success/80 text-sm leading-relaxed">
                  Your documents are being reviewed
                </p>
              </div>
            </div>
            {kycSubmissionDate && (
              <p className="text-sm text-functional-success/70 bg-functional-success/5 px-3 py-2 rounded-lg">
                Submitted on: {formatDate(kycSubmissionDate)}
              </p>
            )}
          </div>

          {/* KYC Details Display */}
          <div className="space-y-4 flex-1">
            <div className="group">
              <label className="text-sm font-medium text-golden-accent uppercase tracking-wide mb-2 block">
                PAN Number
              </label>
              <div className="bg-dark-tertiary/50 px-4 py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
                <span className="text-text-primary font-medium font-mono">
                  {profile?.pan_number || "N/A"}
                </span>
              </div>
            </div>

            <div className="group">
              <label className="text-sm font-medium text-golden-accent uppercase tracking-wide mb-2 block">
                Aadhaar Number
              </label>
              <div className="bg-dark-tertiary/50 px-4 py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
                <span className="text-text-primary font-medium font-mono">
                  {profile?.aadhar_number ? `****-****-${profile.aadhar_number.slice(-4)}` : "N/A"}
                </span>
              </div>
            </div>

            {profile && profile.dob && (
              <div className="group">
                <label className="text-sm font-medium text-golden-accent uppercase tracking-wide mb-2 block">
                  Date of Birth
                </label>
                <div className="bg-dark-tertiary/50 px-4 py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200 flex items-center">
                  <svg className="w-5 h-5 text-functional-info mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-text-primary font-medium">
                    {formatDate(profile.dob)}
                  </span>
                </div>
                {!isProfile && (
                  <Button
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    className="w-full mt-2"
                    variant="primary"
                  >
                    Confirm KYC Details
                  </Button>
                )}
              </div>

            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          {/* Information Banner */}
          <div className="bg-golden-accent/10 rounded-xl p-4 border border-golden-accent/20 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-golden-accent mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="min-w-0 flex-1">
                <h4 className="text-golden-accent font-medium mb-1">Identity Verification Required</h4>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Submit your PAN and Aadhaar details for identity verification.
                  This is required for enhanced access and security.
                </p>
              </div>
            </div>
          </div>

          {/* KYC Form */}
          <div className="flex-1">
            <KycForm
              onSubmit={onSubmit}
              isLoading={isLoading}
              submissionError={error}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default KycSection;
